/**
 * The Socket fleet: every appliance, its HA role, firmware and serial.
 *
 * Two failures are called out above the table because neither is visible in a
 * per-site view: a down HA secondary (the site is fine today and has no
 * failover tomorrow), and firmware skew (one Socket left behind on an old
 * build, which is where odd single-site behaviour usually comes from).
 */

import { Icon, ShieldCheck } from "@norican/icons";
import { Alert, Badge, DataGrid, EmptyState, Typography } from "@norican/react";

import type { NetworkDevice, NetworkSite, NetworkSummary } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatDuration, formatTimestamp } from "./status";

interface SocketFleetProps {
  /** Sites, whose devices are flattened into the fleet table. */
  sites: NetworkSite[];
  /** Estate rollup, for the HA and firmware flags. */
  summary: NetworkSummary;
}

export function SocketFleet({ sites, summary }: SocketFleetProps) {
  const devices = sites.flatMap((site) => site.devices ?? []);
  const haSecondaryDown = summary.ha_secondary_down ?? 0;
  const versions = summary.firmware_versions ?? [];

  return (
    <PanelCard
      title="Socket fleet"
      caption={`${devices.length} ${devices.length === 1 ? "appliance" : "appliances"}`}
      padding="none"
    >
      {(haSecondaryDown > 0 || summary.firmware_skew) && (
        <div className="flex flex-col gap-3 p-4 pb-0">
          {haSecondaryDown > 0 && (
            <Alert tone="danger" title="High availability is not actually available">
              {haSecondaryDown} HA secondary {haSecondaryDown === 1 ? "Socket is" : "Sockets are"}{" "}
              down. Those sites are running on a single appliance — a primary failure takes the site
              offline with nothing to fail over to.
            </Alert>
          )}
          {summary.firmware_skew && (
            <Alert
              tone="warning"
              icon={<Icon icon={ShieldCheck} size="sm" />}
              title="Firmware is not uniform across the fleet"
            >
              {versions.length} versions in service: {versions.join(", ")}. The most common build is
              treated as the baseline; anything else is flagged in the table below.
            </Alert>
          )}
        </div>
      )}

      {devices.length === 0 ? (
        <div className="p-6">
          <EmptyState
            size="sm"
            title="No Sockets reported"
            description="This account has no appliances reporting to Cato."
          />
        </div>
      ) : (
        <div className="h-[28rem]">
          {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
              sticky header only get a scroll region inside a DEFINITE height.
              Without one they latch onto the page viewport and drift while the
              page scrolls. Every DataGrid story wraps the grid this way. */}
          <DataGrid<NetworkDevice>
            aria-label="Cato Socket fleet"
            data={devices}
            getRowId={(device) => `${device.site_id}:${device.id}`}
            getRowSearchText={(device) =>
              `${device.site_name} ${device.name ?? ""} ${device.serial ?? ""} ${device.type ?? ""}`
            }
            density="compact"
            columns={[
              {
                id: "site",
                header: "Site",
                accessor: (device) => device.site_name,
                sortable: true,
                pinned: "left",
                cell: (device) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{device.site_name}</span>
                    <span className="text-fg-muted text-xs">{device.name ?? device.id}</span>
                  </div>
                ),
              },
              {
                id: "model",
                header: "Model",
                accessor: (device) => device.type ?? null,
                sortable: true,
              },
              {
                id: "ha",
                header: "HA role",
                accessor: (device) => device.ha_role ?? null,
                sortable: true,
                cell: (device) =>
                  device.ha_role ? (
                    <Badge
                      tone={
                        device.connected === false
                          ? device.ha_role.toLowerCase() === "secondary"
                            ? "danger"
                            : "warning"
                          : "neutral"
                      }
                      variant="subtle"
                      size="sm"
                    >
                      {device.ha_role}
                    </Badge>
                  ) : (
                    <span className="text-fg-subtle text-xs">standalone</span>
                  ),
              },
              {
                id: "state",
                header: "Connected",
                accessor: (device) => (device.connected === false ? 0 : 1),
                sortable: true,
                cell: (device) => (
                  <Badge
                    tone={
                      device.connected === false
                        ? "danger"
                        : device.connected
                          ? "success"
                          : "neutral"
                    }
                    variant="subtle"
                    size="sm"
                  >
                    {device.connected === false ? "Down" : device.connected ? "Up" : "Unknown"}
                  </Badge>
                ),
              },
              {
                id: "version",
                header: "Firmware",
                accessor: (device) => device.version_number ?? device.version ?? null,
                sortable: true,
                cell: (device) => (
                  <div className="flex items-center gap-2">
                    <Typography variant="code" as="span" className="text-fg text-xs">
                      {device.version_number ?? device.version ?? "—"}
                    </Typography>
                    {device.firmware_outdated && (
                      <Badge tone="warning" variant="subtle" size="xs">
                        behind
                      </Badge>
                    )}
                  </div>
                ),
              },
              {
                id: "release-group",
                header: "Channel",
                accessor: (device) => device.release_group ?? null,
                sortable: true,
              },
              {
                id: "serial",
                header: "Serial",
                accessor: (device) => device.serial ?? null,
                cell: (device) => (
                  <Typography variant="code" as="span" tone="muted" className="text-xs">
                    {device.serial ?? "—"}
                  </Typography>
                ),
              },
              {
                id: "uptime",
                header: "Uptime",
                align: "end",
                numeric: true,
                accessor: (device) => device.uptime_seconds ?? null,
                sortable: true,
                cell: (device) => (
                  <span className="text-fg tabular-nums">
                    {formatDuration(device.uptime_seconds)}
                  </span>
                ),
              },
              {
                id: "last-connected",
                header: "Last connected",
                accessor: (device) => device.last_connected ?? null,
                cell: (device) => (
                  <span className="text-fg-muted text-xs">
                    {formatTimestamp(device.last_connected)}
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
    </PanelCard>
  );
}
